// Application tab of the permit page: the sealed revision as signed (sections 1-8
// of PERMIT_SECTIONS) with its requirements, reviews, approvals and authorisation,
// or the draft before the first submission. Read-only; every status shown here is
// the server's.
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SignatureLine } from '@/components/evidence'
import { Badge, Banner, Button, Card, EmptyState, KeyValue, SectionTitle, Spinner } from '@/components/ui'
import { MEETING_CHECKS, PERMIT_SECTIONS, PERMIT_STATES, SYNTHETIC_NOTICE } from '@/domain/catalogue'
import type { PermitBundle, RevisionRequirement, RevisionView } from '@/domain/types'
import type { PermitViewProps } from '@/features/contracts'
import { fmtDate, fmtDateTime, shortDigest } from '@/lib/format'
import { useRegistry } from '@/lib/queries'
import { LinkButton, StatusBadge, SubHeading, TextBlock } from './bits'
import { EDITABLE_STATES, EXAM_KIND_LABELS, latestReturn, normaliseDraftFields, PLANNING_BASIS_LABELS, RECORD_STATUS, sealedLocation, yesNo } from './model'
import type { Lookup } from './views'
import {
  applicationDates,
  AreaClassificationView,
  ClassActivitiesList,
  classLabel,
  ControlsList,
  DraftSummary,
  GasPlanView,
  IsolationView,
  JhsepView,
  JobReadinessView,
  makeLookup,
  RouteAnswers,
  SimopsView,
  TaskCard,
  taskFromRevision,
  ThirdPartyView,
  WindowText,
} from './views'

/** Revisions sealed before 0015 never asked the third-party question. */
function hasThirdPartyAnswer(content: Record<string, unknown>): boolean {
  return typeof content.third_party === 'object' && content.third_party !== null
}

/** Navigation state the editor sets after a successful submit. */
type SubmittedState = { submitted?: { revision_number: number | null; at: string } }

type Bundle = PermitBundle
type SupportingRecord = Bundle['supporting_records'][number]
type Review = Bundle['reviews'][number]

function versionStatusText(status: string): string {
  return status === 'approved' ? 'approved' : `${status.replace(/_/g, ' ')} - not approved policy`
}

function Section({ no, aside, children }: { no: number; aside?: ReactNode; children: ReactNode }) {
  const s = PERMIT_SECTIONS.find((x) => x.no === no)
  return (
    <Card>
      <SectionTitle no={no} aside={aside}>
        {s?.title ?? `Section ${no}`}
      </SectionTitle>
      {s ? <p className="-mt-2 mb-3 text-xs text-ink-faint">Signed by: {s.signer}</p> : null}
      {children}
    </Card>
  )
}

function PermitFacts({ bundle }: { bundle: Bundle }) {
  const p = bundle.permit
  const current = bundle.revisions.find((r) => r.id === p.current_revision_id)
  const content = current ? normaliseDraftFields(current.content) : bundle.draft ? normaliseDraftFields(bundle.draft.fields) : null
  const items: Array<[ReactNode, ReactNode]> = [
    ['Permit number', <span className="code">{p.permit_number}</span>],
    ['Class', classLabel(p.permit_class)],
    ['Status', PERMIT_STATES[p.lifecycle_state]?.label ?? p.lifecycle_state],
    ...applicationDates(current?.submission.server_signed_at ?? current?.sealed_at ?? null, content?.requested_from ?? null),
    ['Current revision', current ? `Revision ${current.revision_number}, sealed ${fmtDateTime(current.sealed_at)}` : 'None yet (draft)'],
    ['Title', p.title || 'Untitled'],
    ['Geographical area', p.site.name],
    ['Unit', `${p.site_area.name} (${p.site_area.code})`],
    ['Work type', `${p.work_type.description} - ${p.work_type.activity_group}`],
    ['Raised by', `${p.created_by.display_name}${p.created_by.organisation ? ` (${p.created_by.organisation})` : ''}, ${fmtDateTime(p.created_at)}`],
    ['Rules', `${bundle.policy.version_label} - ${bundle.policy.profile_name} (${versionStatusText(bundle.policy.status)})`],
    ['Issuing authority', `${bundle.authority.issuer} - ${bundle.authority.authority_mode.replace(/_/g, ' ')}`],
    ['Authority evidence', bundle.authority.approval_evidence_reference ?? 'None recorded'],
  ]
  if (p.replaces_permit_id) {
    items.push([
      'Replaces',
      <LinkButton to={`/permits/${p.replaces_permit_id}`} variant="secondary">
        Open the earlier permit
      </LinkButton>,
    ])
  }
  return (
    <Card accent={p.permit_class === 'B' ? 'class-b' : 'class-a'}>
      <SectionTitle aside={p.is_synthetic ? <Badge tone="synthetic">synthetic</Badge> : undefined}>Permit</SectionTitle>
      <KeyValue items={items} />
      {p.is_synthetic ? <p className="mt-3 text-xs font-semibold text-synthetic">{SYNTHETIC_NOTICE}</p> : null}
    </Card>
  )
}

function personText(p: { person: { display_name: string; organisation: string | null }; organisation: string | null } | undefined): ReactNode {
  if (!p) return <span className="text-ink-faint">Not recorded</span>
  const org = p.organisation ?? p.person.organisation
  return `${p.person.display_name}${org ? ` (${org})` : ''}`
}

function RecordView({ record }: { record: SupportingRecord }) {
  return (
    <div className="mt-2 rounded-lg border border-line bg-paper p-2">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ink">
          {record.external_identifier} revision {record.external_revision} - {record.issuer}
        </p>
        <StatusBadge status={RECORD_STATUS[record.status] ?? { label: record.status, tone: 'neutral' }} />
      </div>
      <KeyValue
        items={[
          ['Kind', record.record_kind],
          ['Issued', record.reported_issued_at ? fmtDate(record.reported_issued_at) : 'Not stated'],
          ['Expires', record.reported_expires_at ? fmtDate(record.reported_expires_at) : 'Not stated'],
          ['Scope note', <TextBlock text={record.scope_note} empty="None" />],
          ['Evidence reference', <TextBlock text={record.evidence_reference} empty="None" />],
        ]}
      />
      <div className="mt-2">
        <SignatureLine a={record.recorded} label="Entered by (not a verification)" />
      </div>
      {record.verifications.length > 0 ? (
        <div className="mt-2 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Verification history</p>
          {record.verifications.map((v) => (
            <div key={v.id}>
              <p className="text-sm text-ink">
                <StatusBadge status={RECORD_STATUS[v.verification_kind]} /> <TextBlock text={v.findings} />
              </p>
              <div className="mt-1">
                <SignatureLine a={v.attestation} label={v.verification_kind === 'verified' ? 'Verified by' : 'Withdrawn by'} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-ink-soft">Not verified yet.</p>
      )}
    </div>
  )
}

function ReviewView({ review }: { review: Review }) {
  if (review.review_kind === 'return_for_revision') {
    return (
      <div className="rounded-lg border border-warn/40 bg-warn-light p-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold text-ink">Returned for revision</p>
          <Badge tone="warn">{review.change_disposition === 'significant' ? 'Significant change' : review.change_disposition === 'minor' ? 'Minor change' : 'Returned'}</Badge>
        </div>
        <p className="text-sm text-ink">
          <TextBlock text={review.reason} empty="No reason recorded" />
        </p>
        <div className="mt-2">
          <SignatureLine a={review.attestation} label="Returned by" />
        </div>
      </div>
    )
  }
  const known = new Set(MEETING_CHECKS.map((c) => c.code))
  const extra = Object.keys(review.checklist).filter((k) => !known.has(k))
  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-ink">Permit meeting review</p>
        <Badge tone={review.outcome === 'accepted' ? 'ok' : 'warn'}>{review.outcome === 'accepted' ? 'Accepted' : 'Returned'}</Badge>
      </div>
      <KeyValue
        items={[
          ['Planning basis', review.planning_basis ? (PLANNING_BASIS_LABELS[review.planning_basis] ?? review.planning_basis) : 'Not recorded'],
          ['RS visit needed before authorisation', yesNo(review.rs_pre_authorisation_visit_required, 'Not recorded')],
        ]}
      />
      <SubHeading>Checklist</SubHeading>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {[...MEETING_CHECKS.map((c) => [c.code, c.label] as const), ...extra.map((k) => [k, k.replace(/_/g, ' ')] as const)].map(([code, label]) => (
          <li key={code} className="flex items-start justify-between gap-3 p-2 text-sm">
            <span className="text-ink">{label}</span>
            {review.checklist[code] ? <Badge tone="ok">Yes</Badge> : <Badge tone="bad">No</Badge>}
          </li>
        ))}
      </ul>
      <SubHeading>Participants (recorded, not signatures)</SubHeading>
      {review.participants.length === 0 ? (
        <p className="text-sm text-ink-faint">None recorded.</p>
      ) : (
        <ul className="list-inside list-disc text-sm">
          {review.participants.map((p, i) => (
            <li key={i}>
              {p.name}
              {p.role ? ` - ${p.role}` : ''}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <SignatureLine a={review.attestation} label="Responsible Supervisor's review" />
      </div>
    </div>
  )
}

function oacArea(req: Extract<RevisionRequirement, { requirement_kind: 'oac_agreement' }>, lookup: Lookup): string {
  return req.detail.area?.name ?? lookup.area(req.detail.site_area_id)?.name ?? `Area ${req.detail.site_area_id.slice(0, 8)}`
}

function RevisionSections({ bundle, revision, lookup }: { bundle: Bundle; revision: RevisionView; lookup: Lookup }) {
  const c = normaliseDraftFields(revision.content)
  const pa = revision.parties.find((p) => p.role_code === 'PA')
  const ph = revision.parties.find((p) => p.role_code === 'PH')
  const reqs = [...revision.requirements].sort((a, b) => a.sequence - b.sequence)
  const docReqs = reqs.filter((r): r is Extract<RevisionRequirement, { requirement_kind: 'supporting_document' }> => r.requirement_kind === 'supporting_document')
  const oacReqs = reqs.filter((r): r is Extract<RevisionRequirement, { requirement_kind: 'oac_agreement' }> => r.requirement_kind === 'oac_agreement')
  const gasReq = reqs.find((r) => r.requirement_kind === 'gas_testing')
  const examReqs = reqs.filter((r): r is Extract<RevisionRequirement, { requirement_kind: 'worksite_examination' }> => r.requirement_kind === 'worksite_examination')
  const records = bundle.supporting_records.filter((r) => r.revision_id === revision.id)
  const docReqIds = new Set(docReqs.map((r) => r.id))
  const otherRecords = records.filter((r) => !r.requirement_id || !docReqIds.has(r.requirement_id))
  const reviews = bundle.reviews.filter((r) => r.revision_id === revision.id).sort((a, b) => a.attestation.server_signed_at.localeCompare(b.attestation.server_signed_at))
  const approvals = bundle.approvals.filter((a) => a.revision_id === revision.id)
  const pcApprovals = approvals.filter((a) => a.approval_kind === 'pc_approval')
  const exams = bundle.examinations.filter((x) => x.revision_id === revision.id)
  const auths = bundle.authorisations.filter((a) => a.revision_id === revision.id)
  const tasks = [...revision.tasks].sort((a, b) => a.sequence - b.sequence)

  return (
    <div className="space-y-4">
      <Section no={1}>
        <KeyValue
          items={[
            ['Title', <TextBlock text={c.title} />],
            ['Permit class', classLabel(revision.permit_class)],
            ['Why this class: the activities in this work', <ClassActivitiesList codes={c.class_activities} sealed={revision.content.class_activities_detail} />],
            ...(c.class_basis.trim() ? ([['Class basis (free text, earlier form)', <TextBlock text={c.class_basis} />]] as Array<[ReactNode, ReactNode]>) : []),
            ['Work order reference', <TextBlock text={c.work_order_ref} empty="None" />],
            ...applicationDates(revision.submission.server_signed_at ?? revision.sealed_at, c.requested_from),
            ['Requested window', <WindowText from={c.requested_from} to={c.requested_to} />],
            ['Urgency justification', <TextBlock text={c.urgency_justification} empty="None" />],
            ['Applicant (PA)', personText(pa)],
            ['Nominated Permit Holder (PH)', personText(ph)],
            ['Most people in the work party', c.work_party_max !== null ? String(c.work_party_max) : 'Not given'],
          ]}
        />
        {hasThirdPartyAnswer(revision.content) ? (
          <>
            <SubHeading>Third party or subcontractor</SubHeading>
            <ThirdPartyView tp={c.third_party} />
          </>
        ) : null}
        <SubHeading>What will be done</SubHeading>
        <p className="text-sm text-ink">
          <TextBlock text={c.work_description} />
        </p>
        <SubHeading>Work boundary</SubHeading>
        <p className="text-sm text-ink">
          <TextBlock text={c.work_boundary} />
        </p>
        <SubHeading>Tools, equipment and vehicles</SubHeading>
        <p className="text-sm text-ink">
          <TextBlock text={c.tools_equipment} />
        </p>
        <SubHeading>Applicant's submission</SubHeading>
        <SignatureLine a={revision.submission} label="Application and Job HSE plan complete" />
      </Section>

      <Section no={2}>
        <KeyValue
          items={[
            ['Geographical area', bundle.permit.site.name],
            ['Unit', `${revision.site_area.name} (${revision.site_area.code})`],
            ['Exact location', <TextBlock text={c.location_detail} />],
            ['Work type', `${revision.work_type.description} - ${revision.work_type.activity_group}`],
            ['Classification confirmed by the applicant', yesNo(c.location_attributes_confirmed)],
          ]}
        />
        <SubHeading>Area classification sealed with this revision</SubHeading>
        <AreaClassificationView code={sealedLocation(revision.content)?.code ?? null} label={sealedLocation(revision.content)?.label ?? null} register={revision.area_classification} />
        <SubHeading>Other affected units</SubHeading>
        {oacReqs.length === 0 ? (
          <p className="text-sm text-ink-faint">None.</p>
        ) : (
          <ul className="list-inside list-disc text-sm">
            {oacReqs.map((r) => (
              <li key={r.id}>{oacArea(r, lookup)}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section no={3}>
        {tasks.length === 0 ? (
          <p className="text-sm text-ink-faint">No tasks.</p>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => (
              <TaskCard key={t.id} task={taskFromRevision(t)} />
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-ink-soft">
          Procedure status is worked out by the server from verified supporting documents. Activity groups and test types never set the permit class or limits.
        </p>
      </Section>

      <Section no={4}>
        <SubHeading>Job HSE plan</SubHeading>
        <JhsepView jhsep={revision.jhsep} />
        {typeof revision.content.job_readiness === 'object' && revision.content.job_readiness !== null ? (
          <>
            <SubHeading>Job readiness</SubHeading>
            <JobReadinessView jr={c.job_readiness} />
          </>
        ) : null}
        <SubHeading>Hazards and controls</SubHeading>
        <ControlsList items={[...revision.controls].sort((a, b) => a.sequence - b.sequence).map((x) => ({ hazard: x.hazard, control: x.control, kind: x.control_kind }))} />
        <SubHeading>SIMOPS and MOPO</SubHeading>
        <SimopsView simops={c.simops} />
        <SubHeading>Special work</SubHeading>
        <RouteAnswers fields={c} />
        {c.isolation_required === true ? (
          <>
            <SubHeading>Isolation</SubHeading>
            <IsolationView iso={c.isolation} />
          </>
        ) : null}
      </Section>

      <Section no={5}>
        <GasPlanView plan={c.gas_plan} />
        <p className="mt-2 text-sm text-ink">
          <span className="font-semibold">Gas testing requirement on this revision: </span>
          {gasReq ? 'Yes - a completed acceptable test is needed in each cycle before work' : 'None'}
        </p>
      </Section>

      <Section no={6}>
        {docReqs.length === 0 ? <p className="text-sm text-ink-faint">No supporting documents required.</p> : null}
        <div className="space-y-3">
          {docReqs.map((req) => {
            const recs = records.filter((r) => r.requirement_id === req.id)
            return (
              <div key={req.id} className="rounded-lg border border-line bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-ink">{req.detail.kind}</p>
                  {recs.length === 0 ? <Badge tone="warn">No record yet</Badge> : null}
                </div>
                {req.detail.reason ? <p className="text-sm text-ink-soft">{req.detail.reason}</p> : null}
                {recs.map((r) => (
                  <RecordView key={r.id} record={r} />
                ))}
              </div>
            )
          })}
        </div>
        {otherRecords.length > 0 ? (
          <>
            <SubHeading>Other records on this revision</SubHeading>
            {otherRecords.map((r) => (
              <RecordView key={r.id} record={r} />
            ))}
          </>
        ) : null}
      </Section>

      <Section no={7}>
        <SubHeading>Permit meeting</SubHeading>
        {reviews.length === 0 ? (
          <p className="text-sm text-ink-faint">Not reviewed yet.</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <ReviewView key={r.id} review={r} />
            ))}
          </div>
        )}
        <SubHeading>Other affected custodians</SubHeading>
        {oacReqs.length === 0 ? (
          <p className="text-sm text-ink-faint">No other area is affected.</p>
        ) : (
          <div className="space-y-2">
            {oacReqs.map((req) => {
              const agreement = approvals.find((a) => a.approval_kind === 'oac_agreement' && a.affected_site_area?.id === req.detail.site_area_id)
              return (
                <div key={req.id} className="rounded-lg border border-line bg-white p-3">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-ink">{oacArea(req, lookup)}</p>
                    {agreement ? <Badge tone="ok">Agreement recorded</Badge> : <Badge tone="warn">Awaiting agreement</Badge>}
                  </div>
                  {agreement ? (
                    <>
                      <p className="text-sm text-ink">
                        <span className="font-semibold">Added controls: </span>
                        <TextBlock text={agreement.added_controls} empty="None stated" />
                      </p>
                      <div className="mt-2">
                        <SignatureLine a={agreement.attestation} label="Custodian's agreement" />
                      </div>
                    </>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
        {pcApprovals.length > 0 ? (
          <>
            <SubHeading>Production Coordinator</SubHeading>
            {pcApprovals.map((a) => (
              <div key={a.id} className="rounded-lg border border-line bg-white p-3">
                <p className="text-sm text-ink">
                  <TextBlock text={a.notes} empty="No notes" />
                </p>
                <div className="mt-2">
                  <SignatureLine a={a.attestation} label="PC approval (does not replace RS authorisation)" />
                </div>
              </div>
            ))}
          </>
        ) : bundle.class_a ? (
          <>
            <SubHeading>Production Coordinator</SubHeading>
            <p className="text-sm text-ink-soft">
              A Class A permit needs the Production Coordinator's approval of this revision before the RS authorises it. Not recorded yet.
            </p>
          </>
        ) : null}
        <SubHeading>Worksite examinations</SubHeading>
        {examReqs.length === 0 && exams.length === 0 ? <p className="text-sm text-ink-faint">None required or recorded.</p> : null}
        {examReqs.map((req) => (
          <p key={req.id} className="mb-1 text-sm text-ink">
            <span className="font-semibold">Required: </span>
            {EXAM_KIND_LABELS[req.detail.exam_kind] ?? req.detail.exam_kind}
            {req.detail.basis ? ` - ${req.detail.basis}` : ''}
            {req.detail.exam_kind === 'aa_first_day' ? ' (recorded by the RS at the first validation, see Daily cycles)' : ''}
          </p>
        ))}
        <div className="space-y-2">
          {exams.map((x) => (
            <div key={x.id} className="rounded-lg border border-line bg-white p-3">
              <p className="text-sm font-semibold text-ink">
                {EXAM_KIND_LABELS[x.exam_kind] ?? x.exam_kind} - observed {fmtDateTime(x.reported_observed_at)}
              </p>
              <p className="text-sm text-ink">
                <TextBlock text={x.findings} />
              </p>
              <div className="mt-2">
                <SignatureLine a={x.attestation} label="Examined by" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section no={8}>
        {auths.length === 0 ? (
          <p className="text-sm text-ink-faint">Not authorised.</p>
        ) : (
          <div className="space-y-3">
            {auths.map((a) => (
              <div key={a.id} className="rounded-lg border border-line bg-white p-3">
                <KeyValue
                  items={[
                    ['Authorised window', <WindowText from={a.authorisation_from} to={a.authorisation_to} />],
                    ['Kind', a.authorisation_kind.replace(/_/g, ' ')],
                  ]}
                />
                <div className="mt-2">
                  <SignatureLine
                    a={a.attestation}
                    label={a.authorisation_kind === 'extension' ? "Responsible Supervisor's extension approval" : "Responsible Supervisor's authorisation"}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-ink-soft">
          Authorisation allows use only through the daily cycle, after the Operations Manager approval where the policy requires it: RS pre-issue, RS validation and PH acceptance each day. An RS-approved extension moves the authorised end; the OM approval carries over. Extension requests are listed on the permit page.
        </p>
        <SubHeading>Scope digest (SHA-256 of the sealed content)</SubHeading>
        <p className="code break-all text-xs text-ink">{revision.scope_digest}</p>
      </Section>
    </div>
  )
}

function RevisionHistory({ bundle, shownId, onShow, draftShown, onShowDraft, draftAvailable }: {
  bundle: Bundle
  shownId: string | null
  onShow: (id: string) => void
  draftShown: boolean
  onShowDraft: () => void
  draftAvailable: boolean
}) {
  const revisions = [...bundle.revisions].sort((a, b) => b.revision_number - a.revision_number)
  const numberOf = (id: string | null) => bundle.revisions.find((r) => r.id === id)?.revision_number
  return (
    <Card>
      <SectionTitle>Revision history</SectionTitle>
      {revisions.length === 0 && !draftAvailable ? <p className="text-sm text-ink-faint">No revisions yet.</p> : null}
      <ol className="space-y-2">
        {draftAvailable ? (
          <li className="rounded-lg border border-dashed border-line-strong bg-white p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-ink">
                Draft in progress <Badge tone="neutral">not submitted</Badge>
              </p>
              <Button variant="secondary" onClick={onShowDraft} disabled={draftShown}>
                {draftShown ? 'Showing' : 'View'}
              </Button>
            </div>
            {bundle.draft ? (
              <p className="text-xs text-ink-soft">
                Last saved {fmtDateTime(bundle.draft.updated_at)} by {bundle.draft.updated_by.display_name}
              </p>
            ) : null}
          </li>
        ) : null}
        {revisions.map((r) => {
          const returns = bundle.reviews.filter((x) => x.revision_id === r.id && x.review_kind === 'return_for_revision')
          const isCurrent = r.id === bundle.permit.current_revision_id
          const shown = !draftShown && r.id === shownId
          return (
            <li key={r.id} className="rounded-lg border border-line bg-white p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-ink">
                  Revision {r.revision_number} {isCurrent ? <Badge tone="info">current</Badge> : <Badge tone="neutral">earlier</Badge>}
                </p>
                <Button variant="secondary" onClick={() => onShow(r.id)} disabled={shown}>
                  {shown ? 'Showing' : 'View'}
                </Button>
              </div>
              <p className="text-xs text-ink-soft">
                Sealed {fmtDateTime(r.sealed_at)} by {r.submission.signer.display_name} - digest <span className="code">#{shortDigest(r.scope_digest)}</span>
                {r.supersedes_revision_id ? ` - replaces revision ${numberOf(r.supersedes_revision_id) ?? '?'}` : ''}
              </p>
              {returns.map((x) => (
                <p key={x.id} className="mt-1 text-xs text-warn">
                  Returned {fmtDateTime(x.attestation.server_signed_at)} by {x.attestation.signer.display_name}
                  {x.change_disposition ? ` (${x.change_disposition} change)` : ''}: {x.reason ?? 'no reason recorded'}
                </p>
              ))}
            </li>
          )
        })}
      </ol>
    </Card>
  )
}

export function ApplicationView({ bundle }: PermitViewProps) {
  const registryQ = useRegistry()
  const lookup = makeLookup(registryQ.data, bundle)
  const current = bundle.revisions.find((r) => r.id === bundle.permit.current_revision_id) ?? [...bundle.revisions].sort((a, b) => b.revision_number - a.revision_number)[0] ?? null
  const editable = EDITABLE_STATES.includes(bundle.permit.lifecycle_state)
  const draftAvailable = Boolean(bundle.draft) && editable
  const canEdit = bundle.viewer.available_actions.some((a) => a.command === 'revise_permit_draft' && a.enabled)
  const [choice, setChoice] = useState<string | null>(null)
  const showDraft = draftAvailable && (choice === 'draft' || (choice === null && !current))
  const shown = showDraft ? null : (bundle.revisions.find((r) => r.id === choice) ?? current)
  const returned = latestReturn(bundle)
  const returnedNo = returned ? bundle.revisions.find((r) => r.id === returned.revision_id)?.revision_number : undefined
  const submitted = (useLocation().state as SubmittedState | null)?.submitted ?? null

  return (
    <div className="space-y-4">
      {submitted && bundle.permit.lifecycle_state === 'submitted' ? (
        <Banner tone="ok" title="Submitted for review">
          {submitted.revision_number !== null ? `Revision ${submitted.revision_number} was` : 'The application was'} signed and sealed at{' '}
          {fmtDateTime(submitted.at)} and sent to the Responsible Supervisor for the permit meeting. It is not authorised yet, and no work may start
          until it is authorised and the daily cycle is accepted.
        </Banner>
      ) : null}
      <PermitFacts bundle={bundle} />

      {returned ? (
        <Banner tone="warn" title={`Revision ${returnedNo ?? ''} was returned for revision`}>
          {returned.attestation.signer.display_name} returned it on {fmtDateTime(returned.attestation.server_signed_at)}
          {returned.change_disposition ? ` (${returned.change_disposition} change)` : ''}: {returned.reason ?? 'no reason recorded'}. The application must be corrected
          and submitted again; earlier reviews and approvals do not carry over.
          {canEdit ? (
            <div className="mt-2">
              <LinkButton to={`/permits/${bundle.permit.id}/edit`}>Revise the application</LinkButton>
            </div>
          ) : null}
        </Banner>
      ) : bundle.permit.lifecycle_state === 'draft' ? (
        <Banner tone="info" title="Draft - not submitted">
          Nothing in a draft is signed or authorised.
          {canEdit ? (
            <div className="mt-2">
              <LinkButton to={`/permits/${bundle.permit.id}/edit`}>Continue the application</LinkButton>
            </div>
          ) : null}
        </Banner>
      ) : null}

      {shown ? (
        <>
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="display text-lg font-semibold text-ink">
                Revision {shown.revision_number}, sealed {fmtDateTime(shown.sealed_at)}
              </h2>
              {shown.id === bundle.permit.current_revision_id ? <Badge tone="info">current revision</Badge> : <Badge tone="warn">earlier revision - not current</Badge>}
            </div>
            <p className="mt-1 text-xs text-ink-soft">
              This is the application exactly as signed by the applicant. Rules version {bundle.policy.version_label} ({versionStatusText(bundle.policy.status)}).
            </p>
          </Card>
          {shown.id !== bundle.permit.current_revision_id ? (
            <Banner tone="warn">You are looking at an earlier revision. Approvals shown here belong to that revision only.</Banner>
          ) : null}
          <RevisionSections bundle={bundle} revision={shown} lookup={lookup} />
        </>
      ) : showDraft && bundle.draft ? (
        <>
          <Banner tone="info" title="Draft in progress">
            Last saved {fmtDateTime(bundle.draft.updated_at)} by {bundle.draft.updated_by.display_name}. Not signed, not reviewed and not authorised.
          </Banner>
          {registryQ.isLoading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : (
            <>
              {registryQ.error ? (
                <Banner tone="warn">The site register could not be loaded, so some names and the area classification may be missing below.</Banner>
              ) : null}
              <DraftSummary fields={normaliseDraftFields(bundle.draft.fields)} lookup={lookup} siteId={bundle.permit.site.id} permitClass={bundle.permit.permit_class} />
            </>
          )}
        </>
      ) : (
        <EmptyState title="No application content is available to you">The draft is visible only to people who can work on it.</EmptyState>
      )}

      <RevisionHistory
        bundle={bundle}
        shownId={shown?.id ?? null}
        onShow={(id) => setChoice(id)}
        draftShown={showDraft}
        onShowDraft={() => setChoice('draft')}
        draftAvailable={draftAvailable}
      />
    </div>
  )
}
